import { useEffect, useState } from "react";

const COLS = 10;
const ROWS = 4;
const CELL_W = 100;
const CELL_H = 105;

const SUBJECTS = [
  { id: "ADDR-2291-0044", col: 5, row: 2, address: "4307 Avenue G", city: "Austin, TX 78751", coord: "30.3113° N · 97.7330° W", status: "VERIFIED — 3-SOURCE MATCH", tone: "ok", sources: ["Change-of-address registry", "Utility connection footprint", "Property roll"] },
  { id: "HHLD-2291-0119", col: 3, row: 1, address: "4410 Duval St", city: "Austin, TX 78751", coord: "30.3127° N · 97.7368° W", status: "VERIFIED — 2 ADULTS · 1 DEPENDENT", tone: "ok", sources: ["Occupant count reconciliation", "Dependent documentation"] },
  { id: "ADDR-2291-0162", col: 7, row: 0, address: "1201 Manor Rd", city: "Austin, TX 78722", coord: "30.2859° N · 97.7194° W", status: "FIELD VISIT — GEOTAGGED 14:02", tone: "field", sources: ["Signals conflicted", "Field agent dispatched", "Second-operator QA"] },
  { id: "ADDR-2291-0271", col: 8, row: 3, address: "4224 E 43rd St", city: "Austin, TX 78751", coord: "30.3098° N · 97.7301° W", status: "DISCREPANT — UNDECLARED OCCUPANT", tone: "bad", sources: ["Utility footprint mismatch", "Undeclared-occupant signal"] },
];

const TONE = {
  ok: { fill: "#2F4BE0", text: "text-emerald-600", dot: "bg-emerald-500" },
  field: { fill: "#D97706", text: "text-amber-600", dot: "bg-amber-500" },
  bad: { fill: "#DC2626", text: "text-red-600", dot: "bg-red-500" },
};

const STREETS_H = [{ y: 1, name: "Manor Rd" }, { y: 3, name: "E 43rd St" }];
const STREETS_V = [{ x: 2, name: "Duval St" }, { x: 5, name: "Avenue G" }, { x: 8, name: "Red River St" }];

const cellOf = (col, row) => ({ x: col * CELL_W + 8, y: row * CELL_H + 8, w: CELL_W - 16, h: CELL_H - 16 });

const Parcel = ({ col, row, subject, active, onPick }) => {
  const c = cellOf(col, row);
  const isActive = subject && active?.id === subject.id;
  return (
    <rect
      x={c.x} y={c.y} width={c.w} height={c.h} rx="4"
      onMouseEnter={() => subject && onPick(subject)}
      onClick={() => subject && onPick(subject)}
      data-testid={subject ? `site-map-parcel-${subject.id}` : undefined}
      className={subject ? "cursor-pointer transition-colors duration-300" : ""}
      fill={isActive ? TONE[subject.tone].fill : subject ? "#DCE3FB" : "#EEF0F3"}
      fillOpacity={isActive ? 0.2 : 1}
      stroke={isActive ? TONE[subject.tone].fill : "none"}
      strokeWidth="2"
    />
  );
};

const Pin = ({ s }) => {
  const c = cellOf(s.col, s.row);
  const cx = c.x + c.w / 2;
  const cy = c.y + c.h / 2;
  const color = TONE[s.tone].fill;
  return (
    <g key={s.id} pointerEvents="none">
      <circle cx={cx} cy={cy} r="10" fill={color} fillOpacity="0.25">
        <animate attributeName="r" values="10;38;10" dur="2.4s" repeatCount="indefinite" />
        <animate attributeName="fill-opacity" values="0.35;0;0.35" dur="2.4s" repeatCount="indefinite" />
      </circle>
      <circle cx={cx} cy={cy} r="7" fill={color} stroke="#fff" strokeWidth="3" />
    </g>
  );
};

const InfoCard = ({ s }) => (
  <div className="pointer-events-none border-t border-gray-100 bg-white p-4 sm:absolute sm:left-3 sm:top-3 sm:w-[320px] sm:rounded-2xl sm:border sm:border-gray-200 sm:bg-white/95 sm:shadow-xl sm:shadow-black/5 sm:backdrop-blur" data-testid="site-map-card">
    <div className="font-mono-label text-[9px] text-oath-muted">{s.id}</div>
    <div className="mt-1 font-display text-lg font-bold text-oath-ink" data-testid="site-map-address">{s.address}</div>
    <div className="text-sm text-oath-secondary">{s.city}</div>
    <div className="mt-1 font-mono text-[11px] text-oath-muted">{s.coord}</div>
    <div className={`mt-3 flex items-center gap-2 font-mono-label text-[10px] font-semibold ${TONE[s.tone].text}`} data-testid="site-map-status">
      <span className={`h-2 w-2 rounded-full ${TONE[s.tone].dot}`} /> {s.status}
    </div>
    <ul className="mt-3 grid gap-1 border-t border-gray-100 pt-3 text-xs text-oath-secondary">
      {s.sources.map((x) => <li key={x}>● {x}</li>)}
    </ul>
  </div>
);

export const HeroSiteMap = () => {
  const [active, setActive] = useState(SUBJECTS[0]);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) {
      return undefined;
    }
    const t = setInterval(() => {
      setActive((a) => SUBJECTS[(SUBJECTS.findIndex((s) => s.id === a.id) + 1) % SUBJECTS.length]);
    }, 3600);
    return () => clearInterval(t);
  }, [paused]);

  const pick = (s) => {
    setPaused(true);
    setActive(s);
  };

  return (
    <figure className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white" onMouseLeave={() => setPaused(false)} data-testid="hero-site-map">
      <svg viewBox={`0 0 ${COLS * CELL_W} ${ROWS * CELL_H}`} className="block h-auto w-full" role="img" aria-label="Interactive verification site map">
        <rect width="100%" height="100%" fill="#F8F9FB" />
        {Array.from({ length: ROWS }).map((_, r) => Array.from({ length: COLS }).map((__, c) => (
          <Parcel key={`${c}-${r}`} col={c} row={r} subject={SUBJECTS.find((s) => s.col === c && s.row === r)} active={active} onPick={pick} />
        )))}
        {STREETS_H.map((s) => (
          <g key={s.name} pointerEvents="none">
            <line x1="0" x2={COLS * CELL_W} y1={s.y * CELL_H} y2={s.y * CELL_H} stroke="#C7CDD6" strokeWidth="2" strokeDasharray="10 8" />
            <text x={s.y === 1 ? 640 : 250} y={s.y * CELL_H - 6} className="fill-slate-400 font-mono text-[13px]" letterSpacing="1">{s.name}</text>
          </g>
        ))}
        {STREETS_V.map((s) => (
          <g key={s.name} pointerEvents="none">
            <line y1="0" y2={ROWS * CELL_H} x1={s.x * CELL_W} x2={s.x * CELL_W} stroke="#C7CDD6" strokeWidth="2" strokeDasharray="10 8" />
            <text transform={`translate(${s.x * CELL_W - 6} 250) rotate(-90)`} className="fill-slate-400 font-mono text-[13px]" letterSpacing="1">{s.name}</text>
          </g>
        ))}
        <Pin s={active} />
      </svg>
      <InfoCard s={active} />
      <div className="absolute right-3 top-3 flex gap-1.5 rounded-full bg-white/90 px-2 py-1.5" data-testid="site-map-controls">
        {SUBJECTS.map((s) => (
          <button key={s.id} type="button" onClick={() => pick(s)} data-testid={`site-map-dot-${s.id}`} aria-label={s.address}
            className={`h-2.5 rounded-full transition-all ${active.id === s.id ? `w-6 ${TONE[s.tone].dot}` : "w-2.5 bg-gray-300 hover:bg-gray-400"}`} />
        ))}
      </div>
      <figcaption className="flex flex-wrap justify-between gap-2 border-t border-gray-100 px-4 py-2.5 font-mono-label text-[9px] text-oath-muted">
        <span>SITE · 30.2672° N · 97.7431° W</span><span>SCALE · 1:2000 · SURVEY GRID</span><span>FIG. 01 · DWG NO. RP-2291 · HOVER A PARCEL</span>
      </figcaption>
    </figure>
  );
};

const TICKER = [
  ["ADDR-2291-0044", "AUSTIN TX", "VERIFIED"], ["HHLD-2291-0119", "COLUMBUS OH", "VERIFIED"], ["ADDR-2291-0162", "TAMPA FL", "FIELD VISIT"],
  ["HHLD-2291-0208", "SACRAMENTO CA", "VERIFIED"], ["ADDR-2291-0271", "AUSTIN TX", "DISCREPANT"], ["HHLD-2291-0304", "BOISE ID", "VERIFIED"],
];

export const CaseTicker = () => (
  <div className="relative overflow-hidden border-y border-gray-200 bg-white py-3" data-testid="case-ticker">
    <div className="flex w-max animate-[rp-marquee_40s_linear_infinite] gap-10 whitespace-nowrap font-mono-label text-[10px] text-oath-secondary">
      {[...TICKER, ...TICKER].map(([id, city, st], i) => (
        <span key={i}>{id} · {city} · <span className={st === "VERIFIED" ? "text-emerald-600" : st === "DISCREPANT" ? "text-red-600" : "text-amber-600"}>{st}</span></span>
      ))}
    </div>
  </div>
);

export default HeroSiteMap;
